/**
 * PermissionsDialog v8.2 — adds summary card + preset-diff confirm dialog on
 * "Applica preset". Retains v8.1 accordion, per-category counters, sensitive
 * double-confirm, and the embedded mode used by the create-user flow.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Shield, ShieldCheck, AlertTriangle, Search, ChevronRight, CheckCircle2, Circle,
  Sparkles,
} from "lucide-react";
import { computePresetDiff, STATUS_STYLE } from "@/lib/presetDiff";

// -----------------------------------------------------------------------------
// Catalog cache — single fetch per session.
// -----------------------------------------------------------------------------
let _cat = null;
export const loadCatalog = async () => {
  if (_cat) return _cat;
  const r = await api.get("/admin/permissions/catalog");
  _cat = r.data;
  return _cat;
};

const OPEN_BY_DEFAULT = new Set(["staff", "admin"]);

// -----------------------------------------------------------------------------
// Sensitive-key double confirmation.
// -----------------------------------------------------------------------------
function SensitiveConfirmDialog({ open, onOpenChange, items, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="perms-sensitive-confirm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-800">
            <AlertTriangle className="w-5 h-5" />
            Conferma permessi sensibili
          </DialogTitle>
          <DialogDescription>
            Stai per assegnare {items.length} permessi sensibili. Sono
            operazioni potenzialmente irreversibili o ad alto impatto.
          </DialogDescription>
        </DialogHeader>
        <ul className="space-y-2 max-h-60 overflow-y-auto">
          {items.map((p) => (
            <li key={p.key} className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
              <AlertTriangle className="w-4 h-4 text-rose-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="font-medium text-rose-900 text-sm">{p.label}</div>
                <div className="text-xs text-rose-800">{p.description}</div>
              </div>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="perms-sensitive-cancel">
            Annulla
          </Button>
          <Button className="bg-rose-600 hover:bg-rose-700 text-white"
                  onClick={onConfirm}
                  data-testid="perms-sensitive-confirm-btn">
            Confermo e salvo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// PresetDiffConfirmDialog — shows the exact +N / -N before Applica preset runs.
// -----------------------------------------------------------------------------
function PresetDiffConfirmDialog({ open, onOpenChange, addedItems, removedItems,
                                    roleLabel, onConfirm }) {
  const nothingChanges = addedItems.length === 0 && removedItems.length === 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="preset-diff-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-bronze-600" />
            Applica preset {roleLabel && <span className="text-ink-500">· {roleLabel}</span>}
          </DialogTitle>
          <DialogDescription>
            {nothingChanges
              ? "I permessi coincidono già con il preset."
              : "Verifica esattamente cosa cambierà prima di procedere."}
          </DialogDescription>
        </DialogHeader>
        {!nothingChanges && (
          <div className="space-y-4 max-h-[50vh] overflow-y-auto">
            {addedItems.length > 0 && (
              <section data-testid="preset-diff-added">
                <div className="text-sm font-medium text-emerald-800 mb-1">
                  Verranno AGGIUNTI ({addedItems.length})
                </div>
                <ul className="space-y-1">
                  {addedItems.map((p) => (
                    <li key={p.key}
                        className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-sm">
                      <span className="text-emerald-700">+</span>
                      <span className="min-w-0">
                        <b>{p.label}</b>
                        {p.sensitive && (
                          <span className="ml-1 text-[10px] rounded-full px-1.5 py-0.5 bg-rose-100 text-rose-800 border border-rose-300">
                            Sensibile
                          </span>
                        )}
                        <span className="block text-xs text-ink-500">{p.description}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            {removedItems.length > 0 && (
              <section data-testid="preset-diff-removed">
                <div className="text-sm font-medium text-rose-800 mb-1">
                  Verranno RIMOSSI ({removedItems.length})
                </div>
                <ul className="space-y-1">
                  {removedItems.map((p) => (
                    <li key={p.key}
                        className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-sm">
                      <span className="text-rose-700">−</span>
                      <span className="min-w-0">
                        <b>{p.label}</b>
                        {p.sensitive && (
                          <span className="ml-1 text-[10px] rounded-full px-1.5 py-0.5 bg-rose-100 text-rose-800 border border-rose-300">
                            Sensibile
                          </span>
                        )}
                        <span className="block text-xs text-ink-500">{p.description}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                  data-testid="preset-diff-cancel">
            Annulla
          </Button>
          {!nothingChanges && (
            <Button onClick={onConfirm} data-testid="preset-diff-confirm">
              Applica preset
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Category accordion card.
// -----------------------------------------------------------------------------
function CategoryCard({ groupKey, label, items, isSuper, selected, onToggle,
                        onGroupToggle, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const total = items.length;
  const on = items.filter((p) => selected.has(p.key)).length;
  const allOn = on === total && total > 0;
  const noneOn = on === 0;

  return (
    <div className="rounded-xl border border-sand-300 overflow-hidden">
      <button type="button"
              onClick={() => setOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-sand-50"
              data-testid={`perms-cat-toggle-${groupKey}`}>
        <div className="flex items-center gap-2 min-w-0">
          <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
          <span className="font-serif text-base truncate">{label}</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs font-mono text-ink-500"
                data-testid={`perms-cat-count-${groupKey}`}>
            {on} / {total}
          </span>
          {allOn && (
            <span className="inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-800 border border-emerald-200">
              <CheckCircle2 className="w-3 h-3" /> Tutti abilitati
            </span>
          )}
          {noneOn && (
            <span className="inline-flex items-center gap-1 text-[11px] rounded-full px-2 py-0.5 bg-ink-100 text-ink-500 border border-sand-300">
              <Circle className="w-3 h-3" /> Nessun permesso
            </span>
          )}
        </div>
      </button>

      {open && (
        <div className="border-t border-sand-200 p-4">
          {!isSuper && (
            <div className="flex justify-end gap-1 mb-2">
              <button className="text-xs px-2 py-1 rounded border border-sand-300 hover:bg-sand-100"
                      onClick={() => onGroupToggle(items, true)}
                      data-testid={`perms-cat-all-${groupKey}`}>
                Seleziona tutti
              </button>
              <button className="text-xs px-2 py-1 rounded border border-sand-300 hover:bg-sand-100"
                      onClick={() => onGroupToggle(items, false)}
                      data-testid={`perms-cat-none-${groupKey}`}>
                Deseleziona
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-1">
            {items.map((p) => (
              <label key={p.key} title={p.description}
                     className={`flex items-start gap-2 text-sm py-1 ${isSuper ? "opacity-70" : "cursor-pointer"}`}>
                <input type="checkbox" disabled={isSuper}
                       checked={isSuper || selected.has(p.key)}
                       onChange={() => onToggle(p.key)}
                       className="mt-0.5 w-4 h-4"
                       data-testid={`perm-cb-${p.key}`} />
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{p.label}</span>
                  {p.sensitive && (
                    <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] rounded-full px-1.5 py-0.5 bg-rose-100 text-rose-800 border border-rose-300 align-middle"
                          title="Permesso sensibile. Richiede doppia conferma al salvataggio."
                          data-testid={`perm-sensitive-${p.key}`}>
                      <AlertTriangle className="w-2.5 h-2.5" /> Sensibile
                    </span>
                  )}
                  <span className="text-ink-500 text-xs block">
                    {p.description}
                    {p.route && (
                      <span className="ml-2 font-mono text-[10px] text-ink-400">{p.route}</span>
                    )}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Summary card — top of the modal. Never rendered for Super Admin.
// -----------------------------------------------------------------------------
function SummaryCard({ role, diff }) {
  if (!diff) return null;
  const style = STATUS_STYLE[diff.status];
  const isIdentical = diff.status === "identical";
  return (
    <div className={`rounded-xl border px-4 py-3 flex items-center justify-between gap-3 ${style.chip}`}
         data-testid="perms-summary-card"
         data-preset-status={diff.status}>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-1 text-sm">
        <div>
          <div className="text-[10px] uppercase opacity-70">Ruolo</div>
          <div className="font-medium">{role}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase opacity-70">Preset</div>
          <div className="font-mono">{diff.presetSize} permessi</div>
        </div>
        <div>
          <div className="text-[10px] uppercase opacity-70">Attuali</div>
          <div className="font-mono">{diff.currentSize} permessi</div>
        </div>
        <div>
          <div className="text-[10px] uppercase opacity-70">Personalizzazioni</div>
          <div className="font-mono">+{diff.added.length} / −{diff.removed.length}</div>
        </div>
      </div>
      <div className="flex-shrink-0">
        <span className="inline-flex items-center gap-1 text-xs rounded-full px-2 py-1 bg-white/70 border border-current">
          {isIdentical ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
          {isIdentical ? "Conforme" : (diff.status === "modified" ? "Personalizzato" : "Custom")}
        </span>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// PermissionsDialog
// -----------------------------------------------------------------------------
export default function PermissionsDialog({
  user, disabled, onSaved,
  rolePreset = [],
  embedded = false, value, onChange,
  open: openProp, onOpenChange: onOpenChangeProp,
  triggerless = false,
}) {
  const uncontrolled = openProp === undefined;
  const [openU, setOpenU] = useState(false);
  const open = uncontrolled ? openU : openProp;
  const setOpen = uncontrolled ? setOpenU : onOpenChangeProp;

  const [cat, setCat] = useState(null);
  const [selected, setSelected] = useState(new Set(user?.permissions || value || []));
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [sensitiveOpen, setSensitiveOpen] = useState(false);
  const [presetDiffOpen, setPresetDiffOpen] = useState(false);
  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    if (!open) return;
    loadCatalog().then(setCat).catch((e) => toast.error(formatApiError(e)));
    setSelected(new Set(embedded ? (value || []) : (user?.permissions || [])));
    setQ("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (embedded && onChange) onChange(new Set(selected));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const catMap = useMemo(() => Object.fromEntries((cat?.categories || []).map((c) => [c.key, c.label])), [cat]);
  const subMap = useMemo(() => Object.fromEntries((cat?.subcategories || []).map((c) => [c.key, c.label])), [cat]);
  const permByKey = useMemo(() => Object.fromEntries((cat?.permissions || []).map((p) => [p.key, p])), [cat]);
  const effectivePreset = useMemo(
    () => (rolePreset && rolePreset.length ? rolePreset : (cat?.role_presets?.[user?.role] || [])),
    [rolePreset, cat, user?.role]
  );
  const term = q.trim().toLowerCase();

  // Live diff of the currently selected set vs the preset.
  const liveDiff = useMemo(
    () => (isSuper ? null : computePresetDiff([...selected], effectivePreset)),
    [selected, effectivePreset, isSuper]
  );

  const grouped = useMemo(() => {
    if (!cat) return [];
    const match = (p) => !term
      || p.key.toLowerCase().includes(term)
      || p.label.toLowerCase().includes(term)
      || (p.description || "").toLowerCase().includes(term)
      || (p.category || "").toLowerCase().includes(term)
      || (catMap[p.category] || "").toLowerCase().includes(term)
      || (subMap[p.subcategory || ""] || "").toLowerCase().includes(term);

    const items = cat.permissions.filter(match);
    const buckets = new Map();
    for (const p of items) {
      const bKey = `${p.category}::${p.subcategory || ""}`;
      if (!buckets.has(bKey))
        buckets.set(bKey, { category: p.category, subcategory: p.subcategory || null, items: [] });
      buckets.get(bKey).items.push(p);
    }
    return Array.from(buckets.values());
  }, [cat, term, catMap, subMap]);

  const toggle = (k) => {
    if (isSuper) return;
    setSelected((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  };
  const toggleGroup = (items, on) => {
    if (isSuper) return;
    setSelected((s) => { const n = new Set(s); items.forEach((it) => on ? n.add(it.key) : n.delete(it.key)); return n; });
  };

  // Applica preset now runs through a diff-confirmation dialog first.
  const openPresetDiff = () => { if (!isSuper) setPresetDiffOpen(true); };
  const applyPresetNow = () => {
    setSelected(new Set(effectivePreset));
    setPresetDiffOpen(false);
  };

  const initialSet = useMemo(
    () => new Set(embedded ? (value || []) : (user?.permissions || [])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  );
  const sensitiveAdded = useMemo(() => {
    if (!cat || isSuper) return [];
    return cat.permissions.filter(
      (p) => p.sensitive && selected.has(p.key) && !initialSet.has(p.key)
    );
  }, [cat, selected, initialSet, isSuper]);

  // Applica-preset dialog contents.
  const presetVsCurrent = useMemo(() => {
    if (!cat || isSuper) return { addedItems: [], removedItems: [] };
    const P = new Set(effectivePreset);
    const S = new Set(selected);
    const addedItems = [...P].filter((k) => !S.has(k)).map((k) => permByKey[k]).filter(Boolean);
    const removedItems = [...S].filter((k) => !P.has(k)).map((k) => permByKey[k]).filter(Boolean);
    return { addedItems, removedItems };
  }, [cat, selected, effectivePreset, isSuper, permByKey]);

  const doSave = async () => {
    if (embedded) {
      setOpen(false);
      onSaved && onSaved(Array.from(selected));
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}/permissions`,
                      { custom_permissions: Array.from(selected), source: "manual" });
      toast.success("Permessi aggiornati");
      setOpen(false);
      onSaved && onSaved(Array.from(selected));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const onClickSave = () => {
    if (isSuper) { setOpen(false); return; }
    if (sensitiveAdded.length > 0) { setSensitiveOpen(true); return; }
    doSave();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        {!triggerless && (
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="rounded-full" disabled={disabled}
                    data-testid={`perms-btn-${user?.id || "new"}`}>
              <Shield className="w-3 h-3 mr-1" /> Permessi
            </Button>
          </DialogTrigger>
        )}
        <DialogContent className="max-w-3xl p-0 max-h-[92vh] flex flex-col gap-0"
                       data-testid="perms-dialog">
          <DialogHeader className="p-6 pb-3 border-b border-sand-200 flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5" /> Permessi · {user?.email || "nuovo utente"}
            </DialogTitle>
            <DialogDescription>
              Ruolo: <b>{user?.role || "-"}</b>. Il ruolo identifica la figura
              aziendale. I permessi effettivi sono esclusivamente quelli qui
              selezionati.
            </DialogDescription>
          </DialogHeader>

          {isSuper && (
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-3 flex items-start gap-2"
                 data-testid="perms-super-banner">
              <Shield className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-900">
                <div className="font-serif text-base">SUPER ADMIN</div>
                Questo utente dispone implicitamente di <b>tutti i permessi</b>.
                I permessi del Super Admin non sono modificabili.
              </div>
            </div>
          )}

          {!isSuper && (
            <div className="border-b border-sand-200 px-6 pt-3 pb-3 flex-shrink-0 space-y-3">
              <SummaryCard role={user?.role} diff={liveDiff} />
              <div className="relative">
                <Search className="absolute left-3 top-2.5 w-4 h-4 text-ink-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Cerca permesso (nome, chiave, descrizione, categoria)…"
                       className="pl-9 h-10" data-testid="perms-search" />
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto p-6 space-y-3" data-testid="perms-body">
            {!cat && <div className="text-ink-400">Caricamento…</div>}
            {cat && grouped.length === 0 && (
              <div className="text-ink-400 text-sm">Nessun permesso corrisponde alla ricerca.</div>
            )}
            {grouped.map((g) => {
              const label = g.subcategory
                ? `${catMap[g.category] || g.category} · ${subMap[g.subcategory] || g.subcategory}`
                : (catMap[g.category] || g.category);
              const groupKey = `${g.category}-${g.subcategory || "root"}`;
              const defaultOpen = term.length > 0 ? true : OPEN_BY_DEFAULT.has(g.category);
              return (
                <CategoryCard key={groupKey}
                              groupKey={groupKey}
                              label={label}
                              items={g.items}
                              isSuper={isSuper}
                              selected={selected}
                              onToggle={toggle}
                              onGroupToggle={toggleGroup}
                              defaultOpen={defaultOpen} />
              );
            })}
          </div>

          <DialogFooter className="p-4 border-t border-sand-200 flex-shrink-0">
            {!isSuper && (
              <Button variant="outline" onClick={openPresetDiff}
                      data-testid="perms-apply-preset">
                Applica preset ruolo
              </Button>
            )}
            <Button variant="outline" onClick={() => setOpen(false)}>Chiudi</Button>
            {!isSuper && (
              <Button onClick={onClickSave} disabled={busy}
                      data-testid="perms-save">
                {busy ? "Salvo…" : (embedded ? "Conferma" : "Salva permessi")}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SensitiveConfirmDialog open={sensitiveOpen}
                              onOpenChange={setSensitiveOpen}
                              items={sensitiveAdded}
                              onConfirm={() => { setSensitiveOpen(false); doSave(); }} />

      <PresetDiffConfirmDialog open={presetDiffOpen}
                                onOpenChange={setPresetDiffOpen}
                                addedItems={presetVsCurrent.addedItems}
                                removedItems={presetVsCurrent.removedItems}
                                roleLabel={user?.role}
                                onConfirm={applyPresetNow} />
    </>
  );
}
